import { ChangeDetectionStrategy, Component, ElementRef, HostListener, inject, signal } from '@angular/core';

import { AccessibilityService, AccessibilityState } from '@services/accessibility.service';

interface AccessibilityOption {
  flag: keyof Omit<AccessibilityState, 'fontStep'>;
  label: string;
  hint: string;
  icon: string;
}

/**
 * Widget lateral de accesibilidad (#278606, HU 202703 CA3).
 *
 * Botón fijo en el costado derecho que despliega el panel de ajustes: tamaño de letra, modo
 * oscuro, alto contraste, escala de grises, enlaces resaltados, cursor grande y lectura en voz
 * alta. El estado y su aplicación viven en `AccessibilityService`; aquí solo la interfaz.
 *
 * Se monta en los dos layouts (autenticado y público) para que esté en toda la plataforma. No
 * usa el barrel `SHARED_MODULES` ni componentes de ng-zorro: es marcado propio, navegable por
 * teclado (Escape cierra, foco visible, `aria-pressed` en cada opción).
 */
@Component({
  standalone: true,
  selector: 'app-accessibility-widget',
  templateUrl: './accessibility-widget.component.html',
  styleUrls: ['./accessibility-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccessibilityWidgetComponent {
  readonly a11y = inject(AccessibilityService);
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly open = signal(false);

  readonly texts = {
    trigger: 'Abrir opciones de accesibilidad',
    title: 'Accesibilidad',
    close: 'Cerrar',
    fontSize: 'Tamaño de letra',
    decrease: 'Reducir letra',
    increase: 'Aumentar letra',
    reset: 'Restablecer todo',
    speechUnavailable: 'Tu navegador no ofrece lectura en voz alta',
  };

  readonly options: AccessibilityOption[] = [
    { flag: 'darkMode', label: 'Modo oscuro', hint: 'Fondo negro y texto claro', icon: 'moon' },
    { flag: 'highContrast', label: 'Alto contraste', hint: 'Colores más marcados', icon: 'contrast' },
    { flag: 'grayscale', label: 'Escala de grises', hint: 'Quita los colores', icon: 'gray' },
    { flag: 'highlightLinks', label: 'Resaltar enlaces', hint: 'Subraya y enmarca los enlaces', icon: 'link' },
    { flag: 'bigCursor', label: 'Cursor grande', hint: 'Puntero más visible', icon: 'cursor' },
    { flag: 'readAloud', label: 'Lector de pantalla', hint: 'Lee en voz alta lo que enfocas', icon: 'speaker' },
  ];

  get fontPercent(): number {
    return Math.round(this.a11y.fontScale * 100);
  }

  togglePanel(): void {
    this.open.update(v => !v);
  }

  closePanel(): void {
    this.open.set(false);
  }

  toggleOption(option: AccessibilityOption): void {
    this.a11y.toggle(option.flag);
    const activo = this.a11y.state()[option.flag];
    if (option.flag === 'readAloud' && activo) {
      this.a11y.speak(`${option.label} activado`);
    }
  }

  isActive(option: AccessibilityOption): boolean {
    return Boolean(this.a11y.state()[option.flag]);
  }

  isDisabled(option: AccessibilityOption): boolean {
    return option.flag === 'readAloud' && !this.a11y.speechAvailable;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open()) {
      this.closePanel();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) {
      this.closePanel();
    }
  }
}
